import { useEffect, useRef, useState } from 'react';

import { makeStyles } from '@material-ui/core';

import type { metaEvent } from '@rrweb/types';
import type { Replayer, eventWithTime } from 'rrweb';

import { PlaybackControls } from '../PlaybackControls';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
    width: '100%',
  },
  playerWrapper: {
    borderRadius: 'var(--bui-radius-2)',
    overflow: 'hidden',
    background: 'var(--bui-bg-surface-2)',
    border: '1px solid var(--bui-border-disabled)',
    width: '100%',

    // Override rrweb's fixed dimensions so the replay fills the container.
    '& .replayer-container, & .replayer-wrapper, & .replayer-wrapper iframe': {
      width: '100% !important',
      height: '100% !important',
    },
    '& .replayer-wrapper': {
      transform: 'none !important',
    },
  },
});

// rrweb event type constants
const RRWEB_META_EVENT_TYPE = 4;

type SessionReplayPlayerProps = {
  events: eventWithTime[];
  onReplayerReady?: (replayer: Replayer) => void;
};

export const SessionReplayPlayer = ({
  events,
  onReplayerReady,
}: SessionReplayPlayerProps) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [replayer, setReplayer] = useState<Replayer | null>(null);

  useEffect(() => {
    if (!containerRef.current || events.length === 0) return undefined;

    let replayerInstance: Replayer | null = null;
    let disposed = false;

    const init = async () => {
      const { Replayer } = await import('rrweb');
      await import('rrweb/dist/style.css');

      if (disposed || !containerRef.current) return;

      containerRef.current.innerHTML = '';

      replayerInstance = new Replayer(events, {
        root: containerRef.current,
        skipInactive: true,
        showWarning: false,
        blockClass: 'rr-block',
        speed: 1,
        // Never enable UNSAFE_replayCanvas with untrusted data — it adds
        // allow-scripts to the replay iframe sandbox (CVE-2025-45806).
        UNSAFE_replayCanvas: false,
      });

      // Defense-in-depth: ensure the replay iframe is sandboxed so that
      // malicious event payloads cannot execute scripts (CVE-2025-45806).
      if (replayerInstance.iframe) {
        replayerInstance.iframe.setAttribute('sandbox', 'allow-same-origin');
      }

      const meta = events.find(
        (e): e is metaEvent & { timestamp: number } =>
          e.type === RRWEB_META_EVENT_TYPE,
      );
      const recordedHeight = meta?.data.height;
      if (recordedHeight && recordedHeight > 0 && containerRef.current) {
        containerRef.current.style.height = `${recordedHeight}px`;
      }

      setReplayer(replayerInstance);
      onReplayerReady?.(replayerInstance);
      replayerInstance.play();
    };

    init();

    return () => {
      disposed = true;
      if (replayerInstance) {
        replayerInstance.pause();
      }
      setReplayer(null);
    };
  }, [events, onReplayerReady]);

  return (
    <div className={classes.root}>
      <div className={classes.playerWrapper} ref={containerRef} />
      {replayer && <PlaybackControls replayer={replayer} events={events} />}
    </div>
  );
};
